import { forwardRef } from "react";
import { formatTime, resolveUploadUrl } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import CategoryIcon from "@/components/CategoryIcon";

const CATEGORY_HEADLINE = {
  dugun:
    "Hayatımızın en özel gününde sevincimizi paylaşmak, dualarınızla mutluluğumuza mutluluk katmak isteriz — Düğünümüze Davetlisiniz.",
  nisan:
    "Bir ömür sürecek yolculuğumuzun ilk adımında yanımızda olmanız bizi çok mutlu edecek — Nişan Merasimimize Davetlisiniz.",
  kina:
    "Ellerimize yakılacak kına, gönlümüzü sizinle paylaşırken daha da anlam kazanacak — Kına Gecemize Davetlisiniz.",
  sunnet:
    "Yavrumuzun hayatındaki bu özel günde bereketiyle, sevgisiyle yanımızda olmanızı yürekten dileriz — Sünnet Şölenimize Davetlisiniz.",
};

const SocialMediaCard = forwardRef(({ event }, ref) => {
  const { categoryMap, settings } = useSettings();
  if (!event) return null;
  const cat = categoryMap[event.category];
  const headline = CATEGORY_HEADLINE[event.category] || `${cat?.label || "Etkinliğimize"} Davetlisiniz`;
  const date = new Date(event.event_datetime);
  const day = date.getDate().toString().padStart(2, "0");
  const monthShort = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"][date.getMonth()];
  const year = date.getFullYear();

  return (
    <div ref={ref} data-testid="social-media-card"
      className="social-card-bg relative w-full aspect-square max-w-lg mx-auto overflow-hidden rounded-2xl shadow-2xl"
      style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full border-[3px] border-[#D4AF37]/30" />
      <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full border-[2px] border-[#8A252C]/20" />
      <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full border-[3px] border-[#8A252C]/20" />

      <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-[#8A252C]" />
      <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-[#8A252C]" />
      <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-[#8A252C]" />
      <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-[#8A252C]" />

      <div className="relative z-10 h-full flex flex-col items-center justify-between p-10 text-center">
        <div className="flex flex-col items-center">
          {settings?.logo_url && (
            <img
              src={resolveUploadUrl(settings.logo_url)}
              alt={settings.site_name}
              crossOrigin="anonymous"
              className="w-16 h-16 rounded-xl object-cover shadow-md ring-2 ring-[#D4AF37]/40 bg-white mb-3"
            />
          )}
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.3em] uppercase px-3 py-1 rounded-full"
            style={{ color: cat?.accent, backgroundColor: `${cat?.accent}15`, border: `1px solid ${cat?.accent}40` }}>
            <CategoryIcon icons={cat?.icons} />
            {settings?.site_name || "ŞAHDER"} · {cat?.label || event.category}
          </div>
          <h3
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            className="mt-4 text-[18px] leading-snug font-bold text-[#1F2937]"
          >
            {headline}
          </h3>
        </div>

        <div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            className="text-[36px] font-bold text-[#8A252C] leading-tight">
            {event.whose_event}
          </div>
          <div className="mt-1 text-sm font-medium text-[#4B5563] tracking-wide">
            {event.families}
          </div>
        </div>

        <div className="w-full">
          <div className="flex items-stretch justify-center gap-4 mb-4">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A252C]/80">Tarih</div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                className="text-[40px] font-bold leading-none text-[#1F2937] mt-1">
                {day}
              </div>
              <div className="text-xs font-semibold text-[#4B5563] uppercase tracking-widest">{monthShort} {year}</div>
            </div>
            <div className="w-px bg-[#8A252C]/20 mx-2" />
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A252C]/80">Saat</div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                className="text-[40px] font-bold leading-none text-[#1F2937] mt-1">
                {formatTime(event.event_datetime)}
              </div>
              <div className="text-xs font-semibold text-[#4B5563] uppercase tracking-widest">&nbsp;</div>
            </div>
          </div>

          <div className="mt-3 border-t border-[#8A252C]/20 pt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A252C]/80">Mekan</div>
            <div className="text-sm font-semibold text-[#1F2937] mt-0.5">{event.venue}</div>
            <div className="text-xs text-[#4B5563] mt-0.5 line-clamp-2">{event.address}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

SocialMediaCard.displayName = "SocialMediaCard";
export default SocialMediaCard;
